import { useCallback, useEffect, useRef } from 'react'

/**
 * Debounces a callback, delaying it until `delay` ms after the last call. Any pending
 * invocation is dropped when a new one is scheduled, when `cancel` is called, and on unmount.
 * Useful for API calls behind an input while the UI stays immediately responsive.
 *
 * The callback may be async; its promise is fired and forgotten, so the caller owns any
 * error handling inside it.
 *
 * @example
 * // Checkbox with auto-save — the cancel half is optional
 * const [debouncedSave] = useDebouncedCallback(async (value: boolean) => {
 *   await saveToAPI(value)
 * }, 800)
 *
 * @example
 * // Field check that must stop firing once the field is cleared
 * const [checkEmail, cancelEmailCheck] = useDebouncedCallback(async (email: string) => {
 *   setResult(await (await fetch(`/check-email?email=${email}`)).json())
 * }, 400)
 *
 * @template Args - Argument types of the callback
 * @param callback - The callback to debounce (may be async)
 * @param delay - Delay in milliseconds before the callback runs. Default: 300
 * @returns `[debounced, cancel]` — the debounced callback and a canceller for the pending call
 *
 * @see useDebouncedState - For debouncing state updates instead of callbacks
 */
export function useDebouncedCallback<Args extends unknown[]>(
  callback: (...args: Args) => void | Promise<void>,
  delay: number = 300
): [(...args: Args) => void, () => void] {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Keep the ref fresh so the debounced wrapper never calls a stale closure
  const callbackRef = useRef(callback)
  callbackRef.current = callback

  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const debouncedFn = useCallback(
    (...args: Args) => {
      cancel()
      timerRef.current = setTimeout(() => {
        void callbackRef.current(...args)
      }, delay)
    },
    [delay, cancel]
  )

  useEffect(() => cancel, [cancel])

  return [debouncedFn, cancel]
}
